/* ─────────────────────────────────────────────────────────────────────────
   Build a gift — selection state for sections/build-a-gift.liquid.
   - Step A: box size (Pair = 2 / Trio = 3), trimming the selection on change
   - Step B: tile toggling with an index badge; full box → subtle shake
   - Step C: summary slots, discounted total from data-price (cents), one
     /cart/add.js POST for the whole set, then PavosCart.refreshDrawer()
     + openDrawer()

   DISCOUNT_TIERS is a client-side PREVIEW only — it does not apply any
   discount itself. It must mirror the two Shopify Automatic Discounts
   configured in Admin → Discounts (2 items → 10% off, 3 items → 15% off,
   non-combining, 15% ranked above 10% in sort order). If those Admin
   percentages ever change, update this map to match or the preview shown
   here will drift from what checkout actually charges.
   ──────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var NUMBER_WORDS = { 1: 'one', 2: 'two', 3: 'three' };
  var DISCOUNT_TIERS = { 2: 10, 3: 15 }; // keep in sync with the Admin automatic discounts
  var ADD_LABEL = 'Add set to bag';
  var ADDING_LABEL = 'Adding…';

  function formatMoney(cents) {
    var rupees = cents / 100;
    var hasPaise = Math.round(cents) % 100 !== 0;
    try {
      return '₹' + rupees.toLocaleString('en-IN', {
        minimumFractionDigits: hasPaise ? 2 : 0,
        maximumFractionDigits: 2
      });
    } catch (e) {
      return '₹' + (hasPaise ? rupees.toFixed(2) : Math.round(rupees));
    }
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function initSection(root) {
    if (!root || root.hasAttribute('data-gift-ready')) return;
    root.setAttribute('data-gift-ready', '');

    var tiles = Array.prototype.slice.call(root.querySelectorAll('[data-gift-tile]'));
    var boxes = Array.prototype.slice.call(root.querySelectorAll('[data-gift-box]'));
    var slotsEl = root.querySelector('[data-slots]');
    var totalEl = root.querySelector('[data-total]');
    var totalOriginalEl = root.querySelector('[data-total-original]');
    var discountBadgeEl = root.querySelector('[data-discount-badge]');
    var boxNameEl = root.querySelector('[data-summary-box-name]');
    var remainingEl = root.querySelector('[data-remaining]');
    var addBtn = root.querySelector('[data-add-set]');
    var clearBtn = root.querySelector('[data-clear]');
    if (!tiles.length || !slotsEl || !addBtn) return;

    var boxSize = 2;
    var boxName = root.querySelector('[data-gift-box].is-active');
    boxName = (boxName && boxName.getAttribute('data-box-name')) || 'A Duo';
    var selected = []; /* tile elements, in pick order */

    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-gift-panel]'));
    var tabButtons = Array.prototype.slice.call(root.querySelectorAll('[data-gift-tab]'));

    function variantIdOf(tile) {
      return tile.getAttribute('data-variant-id');
    }

    /* The variant ids currently in the set, in pick order. Selection identity is
       the VARIANT, not the element: a product listed in two collections renders
       a tile in each tab, and those twins have to behave as one choice. */
    function selectedIds() {
      return selected.map(variantIdOf);
    }

    /* Slot index this tile occupies, or -1. Matching on variant id rather than
       element identity is what syncs twin tiles across tabs. */
    function slotIndexOf(tile) {
      return selectedIds().indexOf(variantIdOf(tile));
    }

    function renderSlots() {
      slotsEl.textContent = '';
      for (var i = 0; i < boxSize; i += 1) {
        var tile = selected[i];
        var li = el('li', 'build-gift__slot');
        if (tile) {
          var main = el('span', 'build-gift__slot-main');
          main.appendChild(el('span', 'build-gift__slot-num serif-italic', String(i + 1)));
          main.appendChild(
            el('span', 'build-gift__slot-title', tile.getAttribute('data-title') || '')
          );
          li.appendChild(main);
          var cents = parseInt(tile.getAttribute('data-price'), 10) || 0;
          li.appendChild(el('span', 'build-gift__slot-price', formatMoney(cents)));
        } else {
          li.classList.add('build-gift__slot--empty');
          li.appendChild(el('span', 'build-gift__slot-num serif-italic', String(i + 1)));
          li.appendChild(el('span', 'build-gift__slot-empty serif-italic', 'Still unchosen.'));
        }
        slotsEl.appendChild(li);
      }
    }

    /* ── Tab counts ───────────────────────────────────────────────────────────
       How many of THIS tab's candles are in the set. Counted as distinct variant
       ids present in the panel, so a product duplicated inside one collection
       cannot inflate its own tab's number. Kept visible from every tab, because
       switching away from a panel hides the selected tile and would otherwise
       feel like the choice was lost. */
    function renderTabCounts() {
      if (!tabButtons.length) return;
      var ids = selectedIds();

      tabButtons.forEach(function (btn, i) {
        var panel = panels[i];
        var countEl = btn.querySelector('[data-gift-tab-count]');
        if (!panel || !countEl) return;

        var seen = [];
        Array.prototype.forEach.call(panel.querySelectorAll('[data-gift-tile]'), function (tile) {
          var id = variantIdOf(tile);
          if (ids.indexOf(id) !== -1 && seen.indexOf(id) === -1) seen.push(id);
        });

        countEl.textContent = String(seen.length);
        if (seen.length > 0) {
          countEl.removeAttribute('hidden');
          btn.classList.add('has-picks');
        } else {
          countEl.setAttribute('hidden', '');
          btn.classList.remove('has-picks');
        }
      });
    }

    /* ── Tab switching ────────────────────────────────────────────────────────
       Show/hide only. Tiles are never re-created, so selection survives every
       switch — see the note at the top of build-a-gift.liquid. */
    function activateTab(index, focusTab) {
      if (index < 0 || index >= tabButtons.length) return;

      tabButtons.forEach(function (btn, i) {
        var active = i === index;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
        /* Roving tabindex: exactly one tab is in the tab order. */
        btn.setAttribute('tabindex', active ? '0' : '-1');
      });

      panels.forEach(function (panel, i) {
        if (i === index) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });

      if (focusTab) tabButtons[index].focus();
    }

    function initTabs() {
      if (!tabButtons.length) return;

      tabButtons.forEach(function (btn, i) {
        btn.addEventListener('click', function () { activateTab(i, false); });

        btn.addEventListener('keydown', function (event) {
          var next = null;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            next = (i + 1) % tabButtons.length;
          } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            next = (i - 1 + tabButtons.length) % tabButtons.length;
          } else if (event.key === 'Home') {
            next = 0;
          } else if (event.key === 'End') {
            next = tabButtons.length - 1;
          }
          if (next === null) return;
          event.preventDefault();
          activateTab(next, true);
        });
      });
    }

    function render() {
      var full = selected.length >= boxSize;

      tiles.forEach(function (tile) {
        var index = slotIndexOf(tile);
        var isSelected = index !== -1;
        tile.classList.toggle('is-selected', isSelected);
        tile.classList.toggle('is-blocked', full && !isSelected);
        tile.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        var badge = tile.querySelector('[data-slot-badge]');
        if (badge) {
          if (isSelected) {
            badge.textContent = String(index + 1);
            badge.removeAttribute('hidden');
          } else {
            badge.setAttribute('hidden', '');
          }
        }
      });

      boxes.forEach(function (box) {
        var active = parseInt(box.getAttribute('data-box-size'), 10) === boxSize;
        box.classList.toggle('is-active', active);
        box.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      if (boxNameEl) boxNameEl.textContent = boxName;

      renderTabCounts();
      renderSlots();

      var total = selected.reduce(function (sum, tile) {
        return sum + (parseInt(tile.getAttribute('data-price'), 10) || 0);
      }, 0);

      var discountPercent = DISCOUNT_TIERS[boxSize] || 0;
      var discountedTotal = total > 0 ? Math.round(total * (1 - discountPercent / 100)) : 0;

      if (totalEl) {
        totalEl.textContent = total > 0 ? formatMoney(discountedTotal) : '—';
      }
      if (totalOriginalEl) {
        if (total > 0 && discountPercent > 0) {
          totalOriginalEl.textContent = formatMoney(total);
          totalOriginalEl.removeAttribute('hidden');
        } else {
          totalOriginalEl.setAttribute('hidden', '');
        }
      }
      if (discountBadgeEl) {
        if (total > 0 && discountPercent > 0) {
          discountBadgeEl.textContent = discountPercent + '% off — applied automatically at checkout';
          discountBadgeEl.removeAttribute('hidden');
        } else {
          discountBadgeEl.setAttribute('hidden', '');
        }
      }

      var remaining = boxSize - selected.length;
      if (remainingEl) {
        if (remaining > 0) {
          remainingEl.textContent =
            'Choose ' + (NUMBER_WORDS[remaining] || remaining) + ' more.';
          remainingEl.removeAttribute('hidden');
        } else {
          remainingEl.setAttribute('hidden', '');
        }
      }

      addBtn.disabled = !full;
      if (clearBtn) {
        if (selected.length > 0) {
          clearBtn.removeAttribute('hidden');
        } else {
          clearBtn.setAttribute('hidden', '');
        }
      }
    }

    boxes.forEach(function (box) {
      box.addEventListener('click', function () {
        boxSize = parseInt(box.getAttribute('data-box-size'), 10) || 2;
        boxName = box.getAttribute('data-box-name') || boxName;
        selected = selected.slice(0, boxSize);
        render();
      });
    });

    tiles.forEach(function (tile) {
      tile.addEventListener('click', function () {
        /* By variant id, so tapping a twin in another tab deselects the
           original rather than adding the same candle twice. */
        var index = slotIndexOf(tile);
        if (index !== -1) {
          selected.splice(index, 1);
        } else if (selected.length >= boxSize) {
          /* Box is full — ignore, with a subtle shake. */
          tile.classList.remove('is-shake');
          void tile.offsetWidth; /* restart the animation */
          tile.classList.add('is-shake');
          return;
        } else {
          selected.push(tile);
        }
        render();
      });
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        selected = [];
        render();
      });
    }

    addBtn.addEventListener('click', function () {
      if (selected.length !== boxSize) return;

      var items = selected.map(function (tile) {
        return {
          id: parseInt(tile.getAttribute('data-variant-id'), 10),
          quantity: 1
        };
      });

      addBtn.disabled = true;
      addBtn.textContent = ADDING_LABEL;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('add-set failed');
          return response.json();
        })
        .then(function () {
          if (window.PavosCart && typeof window.PavosCart.refreshDrawer === 'function') {
            return window.PavosCart.refreshDrawer().then(function () {
              if (typeof window.PavosCart.openDrawer === 'function') {
                window.PavosCart.openDrawer();
              }
            });
          }
          return null;
        })
        .then(function () {
          selected = [];
          addBtn.textContent = ADD_LABEL;
          render();
        })
        .catch(function () {
          addBtn.disabled = false;
          addBtn.textContent = ADD_LABEL;
        });
    });

    initTabs();
    render();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-build-gift]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();